import type { PostHog } from "posthog-js";
import type {
  ActivityEdits,
  DeviceOverride,
  StripFields,
  TrackPoint,
} from "@/lib/client-convert";
import { resolveTrim, type TrimRange } from "@/lib/trim";
import type { HrFixRange } from "@/lib/hr-fix";

// The raw editor state both converters hold per activity, before gating.
export type EditState = {
  trim: TrimRange;
  strip: StripFields;
  timeShiftMs: number;
  sportOverride?: string;
  deviceOverride?: DeviceOverride;
  hrFixRanges: HrFixRange[];
};

// Assemble the ActivityEdits passed to convertFile from editor state, applying
// the single free/Pro rule: sport and device are free; trim, field-strip,
// time-shift and manual HR fixes are Pro. Free-tier callers pass isPro=false
// (Pro edits no-op); the batch flow is itself Pro and passes isPro=true.
export function buildActivityEdits(
  points: TrackPoint[],
  state: EditState,
  isPro: boolean
): ActivityEdits {
  const r = resolveTrim(points, state.trim);
  const anyStrip = Object.values(state.strip).some(Boolean);
  return {
    trim: isPro && r.isTrimmed ? { startMs: r.startMs, endMs: r.endMs } : undefined,
    strip: isPro && anyStrip ? state.strip : undefined,
    sport: state.sportOverride,
    timeShiftMs: isPro && state.timeShiftMs ? state.timeShiftMs : undefined,
    device: state.deviceOverride,
    hrFixRanges: isPro && state.hrFixRanges.length ? state.hrFixRanges : undefined,
  };
}

// Capture analytics for a finished conversion: the base `file_converted` event
// plus one event per editing feature actually applied. Reading off the
// post-gating ActivityEdits means only edits that reached the output are
// reported. `extra` adds shared context (e.g. { batch: true }) to every event,
// so the conversion event and its edit events can't drift apart.
export function captureConversion(
  posthog: PostHog,
  fromFormat: string,
  toFormat: string,
  edits: ActivityEdits,
  extra: Record<string, unknown> = {}
): void {
  const base = { from_format: fromFormat, to_format: toFormat, ...extra };
  posthog.capture("file_converted", base);
  if (edits.trim) {
    posthog.capture("activity_trimmed", { ...base });
  }
  if (edits.strip) {
    const fields = Object.entries(edits.strip)
      .filter(([, on]) => on)
      .map(([field]) => field);
    posthog.capture("fields_stripped", { ...base, fields, field_count: fields.length });
  }
  if (edits.sport) {
    posthog.capture("sport_changed", { ...base, sport: edits.sport });
  }
  if (edits.timeShiftMs) {
    posthog.capture("time_adjusted", { ...base, shift_ms: edits.timeShiftMs });
  }
  if (edits.device) {
    posthog.capture("device_changed", {
      ...base,
      manufacturer: edits.device.manufacturer,
      has_model: edits.device.product !== undefined,
    });
  }
  if (edits.hrFixRanges?.length) {
    posthog.capture("hr_ranges_fixed", { ...base, range_count: edits.hrFixRanges.length });
  }
}
